import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { uploadImage, formatApiErrorDetail } from "../lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

// Reusable image uploader. Calls onUploaded({ path, url }) on success.
export default function ImageUpload({ onUploaded, label = "Upload image", testid = "image-upload", className = "" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Only JPG, PNG or WEBP images are allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    setBusy(true);
    try {
      const data = await uploadImage(file);
      onUploaded(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        data-testid={testid}
        className={`inline-flex items-center gap-2 text-sm border border-border px-4 py-2 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 ${className}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
}
